import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  storeApp,
  storeuserRole,
  storeAccessUserPlant,
} from "../../Store/TemplateStore/template.action";
import { API_URLS } from "../../network/appUrls";
import { UserExcelStrings } from "../../utils/constantsString";
import {
  getAllUsers,
  exportExcel,
  saveUser,
  getAllPlantsForUser,
} from "../../network/cipAPI";
import {
  faHome,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../../components/AgTable";
import DupontIconButton from "../../components/DupontIconButton";
import DupontButton from "../../components/DupontButton";
import { OverlayLoader } from "../../components/PageLoading";
import { getFormattedDate, updatePlantId } from "../../utils/Utils";
import { dateFormatter } from "../../utils/tableDataFormatter";
import moment from "moment";
import "./index.scss";
import PlantsRender from "../../components/AddCIPAndShutdown/plantsRender";
import ViewPlantCellRenderer from "../../components/AddCIPAndShutdown/ViewPlantCellRenderer";
import DupontDialog from "../../components/DupontDialog";
import { ViewTypeConstants } from "../../constants/global.constants";

const UserPage = () => {
  // Row click handler for plant dialog table
  const handlePlantRowClicked = (event) => {
    const { data } = event;
    if (data.view === ViewTypeConstants.VIEW_EDIT_PLANT) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}editPlant?plantId=${data.plant_id}&isEdit=true`;
      navigate(path, { state: { noOfTrains: data.num_trains } });
    } else if (data.view === ViewTypeConstants.VIEW_ANALYSE_DATA) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}analyseData?plantId=${data.plant_id}&isEdit=true`;
      navigate(path);
    } else if (data.view === ViewTypeConstants.VIEW_HOME) {
      updatePlantId(data.plant_id);
      dispatch(storeAccessUserPlant(true));
      const path = `${API_URLS.basePath}home?plantId=${data.plant_id}`;
      navigate(path);
    }
  };
  const basePath = API_URLS.basePath;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userName = useSelector((state) => state.userName);

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("asc");
  const userEmail = useSelector((state) => state.userEmail);
  const companyName = useSelector((state) => state.companyName);
  const country = useSelector((state) => state.country);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [plantRowData, setPlantRowData] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Handler to open dialog from PlantsRender
  const handleShowPlants = async ({ data }) => {
    setDialogOpen(true);
    setShowOverLayLoader(true);
    try {
      let plants = [];
      if (data && data.email) {
        const response = await getAllPlantsForUser(data.email);
        if (response && response.data) {
          plants = response.data;
        }
      }
      setPlantRowData(plants);
      // setDialogOpen(true);
    } catch (err) {
      setPlantRowData([]);
      setDialogOpen(true);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  // Plant table column definitions for dialog
  const plantColDefs = [
    {
      headerName: t("ROOperationAdvisorTableStrings.name"),
      field: "name",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 200,
    },

    {
      headerName: t("ROOperationAdvisorTableStrings.city"),
      field: "city",
      sortable: true,
      flex: 1,
      minWidth: 175,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.country"),
      field: "country",
      sortable: true,
      flex: 1,
      wrapText: true,
      minWidth: 200,
    },
    {
      headerName: t("ROOperationAdvisorTableStrings.createdon"),
      field: "created_at",
      valueFormatter: dateFormatter,
      sortable: true,
      flex: 1,
      minWidth: 225,
      sort: "desc",
    },
    {
      colId: "viewPlant",
      lockPinned: true,
      headerName: "View Plant",
      field: "viewPlant",
      pinned: "right",
      minWidth: 150,
      cellRenderer: ViewPlantCellRenderer,
    },
  ];
  const handleDialogClose = () => {
    setDialogOpen(false);
    // setDialogRowData(null);
    setPlantRowData([]);
  };

  const formatToLocalDateTime = (dateString) => {
    if (!dateString) return "";

    try {
      const localMoment = moment.utc(dateString).local();

      if (!localMoment.isValid()) {
        return getFormattedDate(dateString);
      }

      return localMoment.format("DD/MM/YYYY, HH:mm:ss");
    } catch (error) {
      console.error("Error formatting date to local time:", error);
      return getFormattedDate(dateString);
    }
  };

  const payload = {
    email: userEmail,
    company: companyName,
    country: country,
    role: null,
    industry: null,
  };

  const saveUserDetails = async (payload) => {
    try {
      let response = await saveUser(payload);
      const { user_role } = response;
      dispatch(storeuserRole(`${user_role}`));
    } catch (error) {
      console.error("Error saving user information:", error);
    }
  };

  const onHomeClicked = () => {
    dispatch(storeApp(null));
    saveUserDetails(payload);
    const path = `${basePath}plant`;
    navigate(path);
  };

  const fetchAccessRequests = async (
    filters = currentFilters,
    sortfield = sortField,
    sortorder = sortOrder
  ) => {
    setShowOverLayLoader(true);
    try {
      const response = await getAllUsers(
        currentPage,
        pageSize,
        filters,
        sortfield,
        sortorder
      );
      if (response && response.data) {
        setRowData(response.data.users || []);
        setTotalPages(response.data.total_pages || 1);
      } else {
        setRowData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching access requests:", error);
      setRowData([]);
      setTotalPages(1);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    fetchAccessRequests(currentFilters, sortField, sortOrder);
  }, [currentPage, pageSize, sortField, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const response = await exportExcel(UserExcelStrings.exportType);
      if (response && response.data) {
        let fileName = t("adminPage.userFileName");
        if (response.headers && response.headers["content-disposition"]) {
          const match = response.headers["content-disposition"].match(
            /filename="?([^";]+)"?/
          );
          if (match && match[1]) fileName = match[1];
        }
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export Excel error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
      fetchAccessRequests(currentFilters, colId, sort);
    } else {
      setSortField("created_at");
      setSortOrder("asc");
      fetchAccessRequests(currentFilters, "created_at", "asc");
    }
  };

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      minWidth: 370,
      sortable: false,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      minWidth: 300,
      sortable: true,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      sort: "desc",
    },
    {
      headerName: t("adminPage.role"),
      field: "user_role",
      minWidth: 350,
      sortable: false,
    },
    {
      colId: "referenceCondition",
      lockPinned: true,
      pinned: "right",
      // width: 200,
      cellRenderer: PlantsRender,
    },
  ];

  return (
    <div className="container-main">
      {showOverLayLoader && <OverlayLoader customHeight={600} />}
      <div className="header_container">
        <div className="home flex-between-center">
          <div>
            <FontAwesomeIcon icon={faHome} onClick={onHomeClicked} />
            <span className="welcome-container">{`${t(
              "welcomeString.welcome"
            )}, ${userName}`}</span>
          </div>
        </div>
        <div className="header-divider" />
        <div className="container-dupont-icon">
          <div className="dupont-icon">
            <div>
              <img
                src={require("../../assets/DuPont_Logo.png")}
                height="30px"
                alt="DuPont Logo"
              />
              <div className="water-solution">
                {t("headerString.waterSolution")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="main-container">
        <div className="admin-title">
          <span>{t("adminPage.userDetails")}</span>
          <DupontButton onClick={handleExportExcel} disabled={exportLoading}>
            {exportLoading
              ? t("adminPage.exporting")
              : t("adminPage.exportExcel")}
          </DupontButton>
        </div>

        <div>
          <AgTable
            columnDefs={colDefs}
            rowData={rowData}
            customHeight="55vh"
            rowHeight={50}
            onSortChanged={onSortChanged}
            hideExport
            context={{ handleReferenceRowClick: handleShowPlants }}
          />
          <DupontDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            title={t("adminPage.plantDetails")}
            isOverlayLoader={showOverLayLoader}
            minWidth={900}
          >
            {
              <AgTable
                columnDefs={plantColDefs}
                rowData={plantRowData}
                customHeight="55vh"
                rowHeight={50}
                hideExport
                context={{
                  ...{ handleReferenceRowClick: handleShowPlants },
                  navigate,
                }}
                onRowClicked={handlePlantRowClicked}
              />
            }
          </DupontDialog>
          <div className="pagination-controls">
            <label htmlFor="pageSize" className="page-size-label">
              {t("adminPage.pageSize")}
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            <DupontIconButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn prev-btn"
              tooltip={t("adminPage.previous")}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </DupontIconButton>
            <span className="pagination-info">
              {t("adminPage.pageInfo", {
                current: currentPage,
                total: totalPages,
              })}
            </span>
            <DupontIconButton
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="pagination-btn next-btn"
              tooltip={t("adminPage.next")}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </DupontIconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPage;
